import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useCollectionBinding,
  useSlotGroup,
  useContentToken,
  usePropertyPhone,
  useTokens,
  usePage,
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHighestContrast, contrastRatio, relativeLuminance } from '../utils/luminance';

interface SpecialOffer {
  title?: string | null;
  description?: string | null;
  disclaimer?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  enabled?: string | null;
  [key: string]: unknown;
}

const specialsCtaDef: SlotGroupDefinition = {
  label: 'Specials CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now →' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/** Living-room gallery photo used as the popup hero image. */
const popupImageDef: SlotGroupDefinition = {
  label: 'Popup Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Living room with a leather sofa, armchair, coffee table, and fireplace.',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

function hasValue(v: unknown): v is string {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== '-' && s !== 'null' && s !== 'undefined' && s !== 'false';
}

function isOfferActive(offer: SpecialOffer | null | undefined): boolean {
  if (!offer) return false;

  // Visibility is driven by content + start/end date window (not `enabled`,
  // which is a static always-false default in the property data).
  if (!hasValue(offer.title) && !hasValue(offer.description)) return false;

  const now = Date.now();

  if (hasValue(offer.start_date)) {
    const start = new Date(offer.start_date!).getTime();
    if (!isNaN(start) && start > now) return false;
  }

  if (hasValue(offer.end_date)) {
    const end = new Date(offer.end_date!).getTime();
    if (!isNaN(end) && end < now) return false;
  }

  return true;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

/** House / home emoji commonly used as special line-item markers. */
const HOUSE_ICON_RE = /^(?:\u{1F3E0}|\u{1F3E1}|\u{1F3D8}\u{FE0F}?|\u{1F3DA}\u{FE0F}?|\u{1F3D7}\u{FE0F}?)(?:\u{FE0F})?$/u;

/**
 * Split richtext/plain special descriptions into line items, preserving
 * intentional breaks from <br>/block tags.
 */
function offerDescriptionLines(html: string): string[] {
  const withBreaks = String(html)
    .replace(/\r\n/g, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, '\n')
    .replace(/<(p|div|li|tr|h[1-6])(?:\s[^>]*)?>/gi, '\n');

  let text: string;
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = withBreaks;
    text = tmp.textContent || tmp.innerText || '';
  } else {
    text = withBreaks.replace(/<[^>]*>/g, '');
  }

  return text
    .split(/\n+/)
    .map((l) => l.replace(/\u00a0/g, ' ').trim())
    .filter(Boolean);
}

/**
 * Merge a lone house-icon line with the following bedroom line, and keep
 * icon + bedroom count as one visual unit so they don't wrap apart.
 */
function coalesceSpecialLines(lines: string[]): Array<{ icon: string | null; text: string }> {
  const out: Array<{ icon: string | null; text: string }> = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Lone house icon on its own line → pull the next line onto it.
    if (HOUSE_ICON_RE.test(line) && i + 1 < lines.length) {
      const next = lines[i + 1];
      out.push({ icon: line, text: next });
      i += 1;
      continue;
    }

    // "🏠 1 Bedroom …" already on one line — split icon from the rest.
    const leading = line.match(
      /^((?:\u{1F3E0}|\u{1F3E1}|\u{1F3D8}\u{FE0F}?|\u{1F3DA}\u{FE0F}?|\u{1F3D7}\u{FE0F}?)(?:\u{FE0F})?)\s+(.+)$/u,
    );
    if (leading) {
      out.push({ icon: leading[1], text: leading[2] });
      continue;
    }

    out.push({ icon: null, text: line });
  }

  return out;
}

function SpecialLineItem({ icon, text }: { icon: string | null; text: string }) {
  const bedMatch = text.match(
    /^((?:Studio|Studios)|(?:\d+)\s*[-–—]?\s*(?:Bed(?:room)?s?|BR))\b(.*)$/i,
  );
  const bedroom = bedMatch?.[1]?.replace(/\s+/g, ' ').trim() ?? null;
  const rest = bedMatch ? bedMatch[2].replace(/^\s*[-–—:]\s*/, '').trim() : '';

  if (!icon && !bedroom) {
    return <div className="sob-modal-line">{text}</div>;
  }

  // Icon + bedroom count are locked together; remaining offer copy may wrap.
  const keepText = bedroom || text;
  const trailing = bedroom ? rest : '';

  return (
    <div className="sob-modal-line">
      <span className="sob-modal-line-keep">
        {icon && (
          <span className="sob-modal-line-icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="sob-modal-line-beds">{keepText}</span>
      </span>
      {trailing ? <span className="sob-modal-line-rest">{trailing}</span> : null}
    </div>
  );
}

const AUTO_OPEN_SESSION_KEY = 'vibesites:special-offer-auto-opened';

/**
 * Popup-style specials (overrides the parent top-strip banner).
 * Floating pill on every page; auto-opens the popup once per session on homepage load.
 * Keeps `--offer-banner-height` at 0 so Header/Hero layout is unaffected.
 */
export function SpecialOfferBanner() {
  const page = usePage();
  const isHomePage = page.alias === 'home' || page.path === '/';

  const offers = useCollectionBinding<SpecialOffer>('specialofferbanner.items', {
    collection: 'special_offers',
  });
  const specialsCta = useSlotGroup('specialofferbanner.specials_cta', specialsCtaDef);
  const showSpecialsCta = hasValue(specialsCta.label);
  const callCtaLabel = useContentToken('specialofferbanner.call_cta_label');
  const propertyPhone = usePropertyPhone();
  const telHref = propertyPhone.replace(/[^+\d]/g, '');
  const showCallCta = hasValue(callCtaLabel) && hasValue(telHref);
  const popupImage = useSlotGroup('specialofferbanner.popup_image', popupImageDef);
  const showPopupImage = hasValue(popupImage.image);

  // Same contrast pick as the parent banner: readable text on secondary.
  const tokens = useTokens();
  const surfaceTextColor = useMemo(() => {
    const secondary = tokens?.color?.secondary;
    if (!secondary) return null;
    const onPrimary = tokens?.color?.textOnPrimary;
    const onSecondary = tokens?.color?.textOnSecondary;
    const candidates = [onSecondary, onPrimary].filter(
      (c): c is string => typeof c === 'string' && c.length > 0,
    );
    const picked = candidates.length ? pickHighestContrast(secondary, candidates) : null;
    if (picked && contrastRatio(secondary, picked) >= 4.5) return picked;
    return relativeLuminance(secondary) > 0.55 ? '#1a1a1a' : '#ffffff';
  }, [tokens]);
  const surfaceStyle = surfaceTextColor
    ? ({ ['--sob-text' as any]: surfaceTextColor } as React.CSSProperties)
    : undefined;

  const activeOffers = useMemo(() => {
    return (offers || []).filter(isOfferActive);
  }, [offers]);

  const hasOffers = activeOffers.length > 0;
  const multi = activeOffers.length > 1;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  // No top banner — keep layout CSS var cleared for Header/Hero consumers.
  useEffect(() => {
    document.documentElement.style.setProperty('--offer-banner-height', '0px');
    return () => {
      document.documentElement.style.setProperty('--offer-banner-height', '0px');
    };
  }, []);

  // Auto-open once per session on the homepage when active offers become available.
  useEffect(() => {
    if (!isHomePage || !hasOffers || hasAutoOpened) return;
    setHasAutoOpened(true);
    try {
      if (sessionStorage.getItem(AUTO_OPEN_SESSION_KEY) === '1') return;
      sessionStorage.setItem(AUTO_OPEN_SESSION_KEY, '1');
    } catch {
      // Private mode / blocked storage — still open once for this mount.
    }
    setModalIdx(0);
    setModalOpen(true);
  }, [isHomePage, hasOffers, hasAutoOpened]);

  // Escape closes the popup.
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const openModal = useCallback(() => {
    setModalIdx(0);
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  // Pill + popup available on every page; only the auto-open is homepage-scoped.
  if (!hasOffers) return null;

  const offer = activeOffers[modalIdx] || activeOffers[0];
  const title = hasValue(offer.title) ? offer.title : '';
  const descriptionLines = hasValue(offer.description)
    ? coalesceSpecialLines(offerDescriptionLines(offer.description))
    : [];
  const rawDisclaimer = offer.disclaimer ?? '';
  const disclaimer = hasValue(rawDisclaimer) ? stripHtml(String(rawDisclaimer)) : '';
  const showReopenButton = !modalOpen;

  return (
    <>
      {/* Floating pill — reopen after the popup is dismissed */}
      <button
        type="button"
        onClick={openModal}
        className="sob-reopen"
        style={{
          ...(surfaceStyle || {}),
          opacity: showReopenButton ? 1 : 0,
          pointerEvents: showReopenButton ? 'auto' : 'none',
          transform: showReopenButton
            ? 'translateX(-50%) translateY(0)'
            : 'translateX(-50%) translateY(16px)',
        }}
        aria-label="Show special offers"
        aria-hidden={!showReopenButton}
        tabIndex={showReopenButton ? 0 : -1}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <span>{activeOffers.length > 1 ? `${activeOffers.length} Special Offers` : 'Special Offer'}</span>
      </button>

      {/* Specials popup */}
      {modalOpen && (
        <div
          className="sob-modal-overlay"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="sob-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Special offers"
            style={surfaceStyle}
          >
            <button type="button" onClick={closeModal} className="sob-modal-close" aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {showPopupImage && (
              <EditableSlotGroup
                groupPath="specialofferbanner.popup_image"
                definition={popupImageDef}
              >
                <div className="sob-modal-media">
                  <VibesitesImage
                    url={popupImage.image || ''}
                    alt={popupImage.alt || ''}
                    className="sob-modal-media-img"
                    sizes="(max-width: 480px) 100vw, 480px"
                  />
                </div>
              </EditableSlotGroup>
            )}

            <EditableCollectionBinding
              bindingPath="specialofferbanner.items"
              label="Special Offers"
            >
              <div className="sob-modal-body" key={modalIdx}>
                {title && <h3 className="sob-modal-title">{title}</h3>}
                {descriptionLines.length > 0 && (
                  <div className="sob-modal-desc">
                    {descriptionLines.map((item, i) => (
                      <SpecialLineItem key={i} icon={item.icon} text={item.text} />
                    ))}
                  </div>
                )}
                {showSpecialsCta && (
                  <EditableSlotGroup
                    groupPath="specialofferbanner.specials_cta"
                    definition={specialsCtaDef}
                  >
                    <a
                      href={specialsCta.url || '/apply-now'}
                      target={specialsCta._target || '_self'}
                      rel={(specialsCta._target || '_self') === '_blank' ? 'noopener noreferrer' : undefined}
                      className="sob-modal-cta"
                      data-vibesite-event="apply_now_clicked"
                    >
                      {specialsCta.label}
                    </a>
                  </EditableSlotGroup>
                )}
                {disclaimer && <p className="sob-modal-disclaimer">{disclaimer}</p>}
                {showCallCta && (
                  <div className="sob-modal-call">
                    <a
                      href={`tel:${telHref}`}
                      className="dniphone dniphonehref sob-modal-call-pill"
                      aria-label={`Call ${propertyPhone}`}
                    >
                      <EditableToken
                        path="specialofferbanner.call_cta_label"
                        as="span"
                      >
                        {callCtaLabel}
                      </EditableToken>
                    </a>
                  </div>
                )}
              </div>
            </EditableCollectionBinding>

            {multi && (
              <div className="sob-modal-nav">
                <button
                  type="button"
                  onClick={() => setModalIdx((modalIdx - 1 + activeOffers.length) % activeOffers.length)}
                  className="sob-modal-arrow"
                  aria-label="Previous offer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <div className="sob-modal-dots">
                  {activeOffers.map((_, i) => (
                    <button
                      type="button"
                      key={i}
                      className={`sob-modal-dot${i === modalIdx ? ' sob-modal-dot--active' : ''}`}
                      onClick={() => setModalIdx(i)}
                      aria-label={`Offer ${i + 1}`}
                      aria-current={i === modalIdx ? 'true' : undefined}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setModalIdx((modalIdx + 1) % activeOffers.length)}
                  className="sob-modal-arrow"
                  aria-label="Next offer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .sob-reopen {
          position: fixed;
          bottom: 1.25rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 90;
          display: flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.6rem 1.25rem;
          border-radius: 9999px;
          border: none;
          background: var(--vibe-color-secondary);
          color: var(--sob-text, var(--vibe-color-text-on-primary));
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.025em;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(0,0,0,0.25);
          transition: opacity 0.35s ease, transform 0.35s ease, box-shadow 0.2s;
        }
        .sob-reopen:hover {
          box-shadow: 0 6px 24px rgba(0,0,0,0.35);
          transform: translateX(-50%) translateY(-3px) !important;
        }

        .sob-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 100;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          background: rgba(0, 0, 0, 0);
          animation: sob-overlay-in 0.3s ease forwards;
        }
        @keyframes sob-overlay-in {
          to { background: rgba(0, 0, 0, 0.5); }
        }

        .sob-modal {
          position: relative;
          width: 100%;
          max-width: 480px;
          background: var(--vibe-color-secondary);
          border-radius: 6px;
          padding: 0 0 1.5rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          opacity: 0;
          transform: translateY(20px) scale(0.97);
          animation: sob-modal-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) 0.05s forwards;
          overflow: hidden;
        }
        @keyframes sob-modal-in {
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .sob-modal-media {
          width: 100%;
          aspect-ratio: 3 / 2;
          overflow: hidden;
          background: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 8%, transparent);
        }

        .sob-modal-media-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .sob-modal-close {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.45);
          background: rgba(0, 0, 0, 0.35);
          color: #fff;
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .sob-modal-close:hover {
          background: rgba(0, 0, 0, 0.55);
          border-color: rgba(255, 255, 255, 0.7);
          color: #fff;
        }

        .sob-modal-body {
          text-align: center;
          padding: 1.5rem 2rem 0;
          animation: sob-fade-in 0.3s ease both;
        }
        @keyframes sob-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .sob-modal-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.35rem;
          font-weight: 700;
          color: var(--sob-text, var(--vibe-color-text-on-primary));
          letter-spacing: 0.02em;
          margin: 0 0 0.75rem;
          line-height: 1.3;
        }

        .sob-modal-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 80%, transparent);
          margin: 0 0 0.75rem;
          line-height: 1.6;
        }

        .sob-modal-line {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: baseline;
          column-gap: 0.35rem;
          row-gap: 0.15rem;
          margin: 0.4rem 0;
        }

        /* House icon + bedroom count stay glued on one line. */
        .sob-modal-line-keep {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .sob-modal-line-icon {
          line-height: 1;
          font-size: 1.05em;
        }

        .sob-modal-line-beds {
          font-weight: 600;
          color: var(--sob-text, var(--vibe-color-text-on-primary));
        }

        .sob-modal-line-rest {
          min-width: 0;
        }

        .sob-modal-cta {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 700;
          color: var(--sob-text, var(--vibe-color-text-on-primary));
          margin: 0.25rem 0 0.75rem;
          line-height: 1.3;
          text-decoration: none;
        }
        .sob-modal-cta:hover {
          text-decoration: underline;
        }

        .sob-modal-disclaimer {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-style: italic;
          color: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 50%, transparent);
          margin: 0;
          line-height: 1.4;
        }

        .sob-modal-call {
          display: flex;
          justify-content: center;
          margin-top: 1rem;
        }

        /* Outline pill — matches footer / header rise-pill-outline */
        .sob-modal-call-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          max-width: 260px;
          padding: 0.625rem 1.25rem;
          border-radius: 9999px;
          border: 1.5px solid currentColor;
          background-color: transparent;
          color: var(--sob-text, var(--vibe-color-text-on-primary));
          font-family: var(--vibe-font-body);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          line-height: 1.3;
          text-align: center;
          text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease, transform 0.2s ease;
        }
        .sob-modal-call-pill:hover {
          transform: translateY(-1px);
          background-color: color-mix(in srgb, currentColor 12%, transparent);
        }

        .sob-modal-nav {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin: 1.25rem 2rem 0;
          padding-top: 1rem;
          border-top: 1px solid color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 20%, transparent);
        }

        .sob-modal-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 30%, transparent);
          background: transparent;
          color: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 70%, transparent);
          cursor: pointer;
          transition: border-color 0.2s, color 0.2s, background 0.2s;
        }
        .sob-modal-arrow:hover {
          border-color: var(--sob-text, var(--vibe-color-text-on-primary));
          color: var(--sob-text, var(--vibe-color-text-on-primary));
          background: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 12%, transparent);
        }

        .sob-modal-dots {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .sob-modal-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 1.5px solid color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 50%, transparent);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .sob-modal-dot:hover {
          border-color: var(--sob-text, var(--vibe-color-text-on-primary));
          transform: scale(1.2);
        }
        .sob-modal-dot--active {
          background: var(--sob-text, var(--vibe-color-text-on-primary));
          border-color: var(--sob-text, var(--vibe-color-text-on-primary));
        }

        @media (prefers-reduced-motion: reduce) {
          .sob-reopen { transition: none; }
          .sob-modal-overlay, .sob-modal, .sob-modal-body {
            animation: none;
            opacity: 1;
            transform: none;
          }
          .sob-modal-overlay { background: rgba(0,0,0,0.5); }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SpecialOfferBanner',
  displayName: 'Special Offer Popup',
  tokens: {
    'specialofferbanner.call_cta_label': {
      label: 'Call Now Label',
      type: 'text',
      defaultValue: 'Call Now',
    },
  },
  slotGroups: {
    'specialofferbanner.specials_cta': specialsCtaDef,
    'specialofferbanner.popup_image': popupImageDef,
  },
  collectionBindings: {
    'specialofferbanner.items': {
      label: 'Special Offers',
      collection: 'special_offers',
    },
  },
};

(SpecialOfferBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SpecialOfferBanner;
